import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import EmailInput from './EmailInput';
import RemoveProviderModal from './RemoveProviderModal';

/**
 * @typedef {import('../types').RosterProvider} RosterProvider
 */

/**
 * @param {{ label: string, children: React.ReactNode }} props
 */
const RosterCardRow = ({ label, children }) => (
  <div className="flex">
    <div className="w-2/5 bg-blue-05 px-4 py-4">
      <span className="text-sm font-semibold text-deep-blue-70">{label}</span>
    </div>
    <div className="w-3/5 min-w-0 bg-white px-4 py-4 text-gray-90">
      {children}
    </div>
  </div>
);

RosterCardRow.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

/**
 * Displays a provider's information in a card format, used for mobile view.
 * @param {{
 *  provider: RosterProvider,
 *  onProviderChange: (providerCode: string, updates: Partial<RosterProvider>) => void,
 *  onRemove: (providerId: string) => void,
 *  disabled?: boolean,
 *  existingProviders?: Array<RosterProvider>
 * }} props
 */
const ProviderRosterCard = ({
  provider,
  onProviderChange,
  onRemove,
  disabled = false,
  existingProviders = [],
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleEmailInputChange = useCallback(
    /**
     * @param {string} value
     */
    (value) => {
      onProviderChange(provider.providerCode, { email: value });
    },
    [onProviderChange, provider.providerCode],
  );

  const handleEmailValidityChange = useCallback(
    /**
     * @param {boolean} isValid
     */
    (isValid) => {
      onProviderChange(provider.providerCode, { validEmail: isValid });
    },
    [onProviderChange, provider.providerCode],
  );

  const handleRemoveClick = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleConfirmRemove = useCallback(() => {
    onRemove(provider.providerCode);
  }, [onRemove, provider.providerCode]);

  // Filter out the current provider from the list to check for duplicates
  const otherProviders = useMemo(() => {
    return existingProviders.filter(
      (p) => p.providerCode !== provider.providerCode,
    );
  }, [existingProviders, provider.providerCode]);

  return (
    <>
      <div
        className="flex flex-col overflow-hidden rounded-lg border border-gray-20 bg-white"
        data-testid="provider-roster-card"
      >
        <RosterCardRow label="Provider Name">
          <div className="flex gap-2">
            <div className="flex-1">
              <p className="text-sm font-bold">{provider.displayName}</p>
              <p className="text-sm font-thin mb-1">
                {provider.primarySpecialty?.specialtyName}
              </p>
              <p className="text-sm font-thin">NPI: {provider.npi}</p>
            </div>
            <div className="flex items-start">
              <button
                type="button"
                onClick={handleRemoveClick}
                className="min-w-6 min-h-6 cursor-pointer focus:outline-none focus:ring-2 focus:ring-coral-90 focus:ring-opacity-50 rounded"
                aria-label={`Remove ${provider.displayName} from roster`}
                data-testid="remove-provider-button"
                disabled={disabled}
              >
                <img
                  src="/public/images/trash-icon.svg"
                  alt=""
                  className="min-w-6 min-h-6"
                />
              </button>
            </div>
          </div>
        </RosterCardRow>

        <RosterCardRow label="Primary Practice/Office">
          <div>
            <p className="text-sm font-semibold">{provider.office?.name}</p>
            <p className="text-sm font-extralight">
              {provider.office?.addressLine}
              <br />
              {provider.office?.cityState}
            </p>
          </div>
        </RosterCardRow>

        <RosterCardRow label="Email Address">
          <EmailInput
            value={provider.email || ''}
            onValueChange={handleEmailInputChange}
            onValidityChange={handleEmailValidityChange}
            disabled={disabled}
            otherProviders={otherProviders}
            providerEmail={provider.emailFromApi ?? ''}
          />
        </RosterCardRow>
      </div>

      <RemoveProviderModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmRemove}
        providerName={provider.displayName}
      />
    </>
  );
};

ProviderRosterCard.propTypes = {
  provider: PropTypes.shape({
    providerCode: PropTypes.string,
    displayName: PropTypes.string,
    npi: PropTypes.string,
    providerImageUrl: PropTypes.string,
    primarySpecialty: PropTypes.shape({
      specialtyName: PropTypes.string,
      specialtyCode: PropTypes.string,
    }),
    office: PropTypes.shape({
      name: PropTypes.string,
      addressLine: PropTypes.string,
      cityState: PropTypes.string,
      phone: PropTypes.string,
      fax: PropTypes.string,
    }),
    productCode: PropTypes.string,
    email: PropTypes.string,
    validEmail: PropTypes.bool,
  }).isRequired,
  onProviderChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  existingProviders: PropTypes.arrayOf(PropTypes.object),
};

export default ProviderRosterCard;
